import { getTheatricalRelease } from 'src/services/Theatrical'
import { TheatricalPayItForwardView } from 'src/views/PayItForward/TheatricalPayItForwardView'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { PayItForwardOfferDataProvider } from '@/contexts/PayItForwardOffersDataContext/PayItForwardOffersDataContext'
import { runExperimentsOnServer } from '@/experimentation/server'
import { getBasicPageMetaData, Seo } from '@/layout/Seo'
import { DefaultLayout } from '@/layouts/DefaultLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { MarketingVideoPopup } from '@/organisms/MarketingVideoPopup'
import { getIndexForOfferThatImpactsClosestTo100People, getOffersForProject, Offer } from '@/services/PifService'
import { TheatricalReleaseResult } from '@/services/Theatrical/queries'
import { TheatricalReleaseRegion } from '@/types/codegen-federation'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import DatadogRumFull from '@/utils/datadog/DatadogRumFull'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { DavidPifView } from '@/views/PayItForward/DavidPifView/DavidPifView'

interface PayItForwardProjectCheckoutPageProps {
  cloudinaryImageTransformation: string
  initialOfferIndex: number
  offers: Offer[]
  locale: string
  theatricalRelease: TheatricalReleaseResult
  theatricalSlug: string
  newPifLayout?: boolean
  pifNameInCreditsEnabled?: boolean
  isDavidPifView?: boolean
}

export const PayItForwardProjectCheckoutPage: NextPageWithLayout<PayItForwardProjectCheckoutPageProps> = ({
  cloudinaryImageTransformation,
  locale,
  offers,
  theatricalRelease,
  theatricalSlug,
  newPifLayout,
  pifNameInCreditsEnabled,
  isDavidPifView,
}) => {
  const { t } = useTranslate('pif')
  const theatricalName = theatricalRelease?.title

  return (
    <DefaultLayout
      regions={theatricalRelease?.theatricalReleaseRegions as TheatricalReleaseRegion[]}
      isDarkMode={false}
      siteNavGradient={isDavidPifView === true && theatricalSlug === slugs.david}
      hideSaleBanner={isDavidPifView === true && theatricalSlug === slugs.david}
      hideTicketsNavBanner={isDavidPifView === true && theatricalSlug === slugs.david}
    >
      <DatadogRumFull />
      <div className="bg-core-gray-100">
        <Seo
          {...getBasicPageMetaData({
            title:
              theatricalSlug === slugs.david
                ? 'Help 1 Million Kids See DAVID in Theaters This Christmas'
                : t('pifTheatricalTitleAngel', 'Pay it Forward for {{theatricalName}} on Angel', {
                    theatricalName,
                  }),
            description:
              theatricalSlug === slugs.david
                ? 'Join the movement to send 1 million kids to see DAVID: The Animated Movie in theaters for free. Pay it Forward and share a story of faith and courage.'
                : t(
                    'pifTheatricalDescription',
                    'By Paying it Forward to {{theatricalName}}, you help audiences see this deeply moving and monumental movie for free. Help us get this movie to as many people as possible.',
                    { theatricalName },
                  ),
            cloudinaryImagePath:
              theatricalSlug === slugs.david
                ? 'v1761851105/david/open-graph-david-251030b.jpg'
                : `${theatricalRelease?.region?.seoImage}.webp`,
            cloudinaryTransformation: cloudinaryImageTransformation,
            locale,
            path: `${paths.payItForward.index}/${theatricalSlug}`,
          })}
        />
        <PayItForwardOfferDataProvider offers={offers}>
          <MarketingVideoPopup />

          {isDavidPifView ? (
            <DavidPifView theatricalRelease={theatricalRelease} />
          ) : (
            <TheatricalPayItForwardView
              theatricalRelease={theatricalRelease}
              newPifLayout={newPifLayout}
              pifNameInCreditsEnabled={pifNameInCreditsEnabled}
            />
          )}
        </PayItForwardOfferDataProvider>
      </div>
    </DefaultLayout>
  )
}

export const getServerSideProps = getServerSidePropsErrorHandler<PayItForwardProjectCheckoutPageProps>(
  async ({ federationClient, locale, next404, params, req, res }) => {
    const slug = getItemOrFirstEntry(params?.slug)

    if (!slug) return next404()

    const [offers, theatricalRelease, translations] = await Promise.all([
      getOffersForProject({ projectSlug: slug }, federationClient),
      getTheatricalRelease({ theatricalSlug: slug, client: federationClient }),
      loadTranslations(locale, ['common', 'pif', 'account']),
    ])

    if (!theatricalRelease || !offers?.length) return next404()

    const experiments =
      slug === slugs.david
        ? await runExperimentsOnServer(req, res, [
            { experimentKey: 'david-pif-old-vs-new-v2', defaultValue: false },
            { experimentKey: 'pif-name-in-credits', defaultValue: false },
            { experimentKey: 'bespoke-david-pif', defaultValue: false },
          ])
        : null

    const newPifLayout = Boolean(experiments?.['david-pif-old-vs-new-v2'])
    const pifNameInCreditsEnabled = Boolean(experiments?.['pif-name-in-credits'])
    const isDavidPifView = Boolean(experiments?.['bespoke-david-pif']) && slug === slugs.david

    const initialOfferIndex = getIndexForOfferThatImpactsClosestTo100People(offers)
    const cloudinaryImageTransformation = 'c_scale'

    return {
      props: {
        cloudinaryImageTransformation,
        initialOfferIndex,
        locale,
        offers,
        theatricalRelease,
        theatricalSlug: slug,
        newPifLayout,
        pifNameInCreditsEnabled,
        isDavidPifView,
        ...translations,
      },
    }
  },
)

PayItForwardProjectCheckoutPage.getLayout = (page) => {
  return page
}

export default PayItForwardProjectCheckoutPage
